import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Package, DollarSign, Clock, Eye, Edit, Trash2, Upload, X, Image, Save, Tag, FileText, CheckCircle, AlertCircle, XCircle, Zap } from 'lucide-react';
import { toast } from 'sonner';
import BoostModal from '../components/BoostModal';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const resolveImg = (url) => url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const DealerDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [vehicles, setVehicles] = useState([]);
  const [carRequests, setCarRequests] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [paymentSearch, setPaymentSearch] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [boosts, setBoosts] = useState([]);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [boostingVehicle, setBoostingVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);
  
  const emptyForm = {
    make: '', model: '', year: new Date().getFullYear(), vin: '', mileage: '',
    condition: 'clean', location: '', body_type: 'sedan', damage_type: '',
    description: '', transmission: 'Automatic', fuel_type: 'Gasoline',
    color: 'Black', drivetrain: 'FWD', engine_size: '2.0L', images: [],
    listing_type: 'auction', price: '', currency: 'USD'
  };
  
  const [formData, setFormData] = useState(emptyForm);
  const [auctionData, setAuctionData] = useState({ start_price: '', buy_now_price: '', duration_hours: 72 });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadingVehicle, setUploadingVehicle] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Verification state
  const [verificationDocs, setVerificationDocs] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState('unverified');
  const [rejectionReason, setRejectionReason] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showVerificationSection, setShowVerificationSection] = useState(false);
  const [submittingVerification, setSubmittingVerification] = useState(false);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  
  // Contact Info state
  const [contactInfo, setContactInfo] = useState({ phone: '', whatsapp: '', email: '' });
  const [editingContact, setEditingContact] = useState(false);
  const [savingContact, setSavingContact] = useState(false);

  // Bank / Payout state
  const [banks, setBanks] = useState([]);
  const [bankDetails, setBankDetails] = useState({
    bank_name: '', bank_code: '', account_number: '', account_name: '',
    business_name: '', paystack_subaccount_code: null, has_subaccount: false,
  });
  const [editingBank, setEditingBank] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [resolvingAccount, setResolvingAccount] = useState(false);
  const [resolvedAccountName, setResolvedAccountName] = useState('');
  const [bankFormError, setBankFormError] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user && user.role !== 'dealer' && user.role !== 'admin') {
      navigate('/dashboard');
    } else if (user) {
      fetchDealerData();
    }
  }, [user, authLoading, navigate]);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchDealerData = async () => {
    try {
      const [vehiclesRes, requestsRes, reservationsRes, paymentsRes, paymentStatsRes, boostsRes, docsRes, contactRes, bankRes, banksRes] = await Promise.all([
        axios.get(`${API}/dealers/my-vehicles`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/dealer/vehicle-requests`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/dealer/reservations`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/dealer/payments`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/dealer/payments/stats`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/dealer/boosts`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/dealers/my-documents`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/dealers/contact-info`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/dealers/me/bank-details`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/paystack/banks`, { headers: getHeaders(), withCredentials: true })
      ]);
      setVehicles(vehiclesRes.data || []);
      setCarRequests(requestsRes.data || []);
      setReservations(reservationsRes.data || []);
      setPayments(paymentsRes.data || []);
      setPaymentStats(paymentStatsRes.data || {});
      setBoosts(boostsRes.data || []);
      setVerificationDocs(docsRes.data?.documents || []);
      setVerificationStatus(docsRes.data?.verification_status || 'unverified');
      setRejectionReason(docsRes.data?.rejection_reason || null);
      setContactInfo({
        phone: contactRes.data?.phone || '',
        whatsapp: contactRes.data?.whatsapp || '',
        email: contactRes.data?.email || ''
      });
      setBankDetails({
        bank_name: bankRes.data?.bank_name || '',
        bank_code: bankRes.data?.bank_code || '',
        account_number: bankRes.data?.account_number || '',
        account_name: bankRes.data?.account_name || '',
        business_name: bankRes.data?.business_name || '',
        paystack_subaccount_code: bankRes.data?.paystack_subaccount_code || null,
        has_subaccount: !!bankRes.data?.has_subaccount,
      });
      setResolvedAccountName(bankRes.data?.account_name || '');
      setBanks(banksRes.data?.banks || []);
    } catch (error) {
      console.error('Error fetching dealer data:', error);
      // Ensure arrays are set to empty on error
      setVehicles([]);
      setCarRequests([]);
      setReservations([]);
      setPayments([]);
      setVerificationDocs([]);
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/vehicle-requests/${requestId}/status`, 
        { status: newStatus }, 
        { headers: getHeaders(), withCredentials: true }
      );
      fetchDealerData();
    } catch (error) {
      alert('Failed to update request status');
    }
  };

  const updateReservationStatus = async (reservationId, newStatus) => {
    try {
      await axios.patch(`${API}/reservations/${reservationId}/status?status=${newStatus}`,
        {},
        { headers: getHeaders(), withCredentials: true }
      );
      fetchDealerData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update reservation status');
    }
  };
  
  const handleSaveContactInfo = async () => {
    setSavingContact(true);
    try {
      const response = await axios.put(`${API}/dealers/contact-info`, contactInfo, {
        headers: getHeaders(),
        withCredentials: true
      });
      
      toast.success('Contact information saved successfully!');
      setEditingContact(false);
      
      // Update local state with saved data
      setContactInfo(response.data.contact_info);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contact information');
    } finally {
      setSavingContact(false);
    }
  };

  // === Bank / Payout Handlers ===
  const handleBankSelect = (e) => {
    const code = e.target.value;
    const bank = banks.find((b) => b.code === code);
    setBankDetails((prev) => ({
      ...prev,
      bank_code: code,
      bank_name: bank ? bank.name : '',
    }));
    setResolvedAccountName('');
    setBankFormError('');
  };

  const handleResolveAccount = async () => {
    setBankFormError('');
    if (!bankDetails.bank_code || !bankDetails.account_number || bankDetails.account_number.length < 10) {
      setBankFormError('Select a bank and enter a 10-digit account number.');
      return;
    }
    setResolvingAccount(true);
    try {
      const res = await axios.post(
        `${API}/paystack/resolve-account`,
        { account_number: bankDetails.account_number, bank_code: bankDetails.bank_code },
        { headers: getHeaders(), withCredentials: true }
      );
      setResolvedAccountName(res.data.account_name || '');
      setBankDetails((prev) => ({ ...prev, account_name: res.data.account_name || '' }));
    } catch (err) {
      setResolvedAccountName('');
      setBankFormError(err.response?.data?.detail || 'Could not verify account. Check bank & number.');
    } finally {
      setResolvingAccount(false);
    }
  };

  const handleSaveBankDetails = async () => {
    setBankFormError('');
    if (!bankDetails.bank_code || !bankDetails.account_number || !bankDetails.business_name) {
      setBankFormError('Bank, account number and business name are required.');
      return;
    }
    if (!resolvedAccountName) {
      setBankFormError('Please verify the account first (click Verify).');
      return;
    }
    setSavingBank(true);
    try {
      const res = await axios.put(
        `${API}/dealers/bank-details`,
        {
          bank_name: bankDetails.bank_name,
          bank_code: bankDetails.bank_code,
          account_number: bankDetails.account_number,
          business_name: bankDetails.business_name,
        },
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success(res.data?.message || 'Bank details saved');
      setEditingBank(false);
      await fetchDealerData();
    } catch (err) {
      setBankFormError(err.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setSavingBank(false);
    }
  };

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleAuctionChange = (e) => setAuctionData({ ...auctionData, [e.target.name]: e.target.value });

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploadingImages(true);
    const newImages = [...uploadedImages];
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API}/upload-vehicle-image`, fd, {
          headers: getHeaders(),
          withCredentials: true
        });
        newImages.push(res.data.url);
      } catch (err) {
        console.error('Image upload error:', err);
        alert(`Failed to upload ${file.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    setUploadedImages(newImages);
    setUploadingImages(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => setUploadedImages(uploadedImages.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploadedImages.length === 0) { alert('Please add at least one image'); return; }
    setUploadingVehicle(true);

    try {
      const payload = {
        ...formData, images: uploadedImages,
        mileage: parseInt(formData.mileage), year: parseInt(formData.year),
        price: formData.listing_type === 'direct_sale' ? parseFloat(formData.price) || 0 : null,
        currency: formData.currency || 'USD'
      };

      if (editingVehicle) {
        await axios.put(`${API}/vehicles/${editingVehicle.vehicle_id}`, payload, { headers: getHeaders(), withCredentials: true });
        // Update auction if this is an auction listing
        if (formData.listing_type === 'auction' && (auctionData.start_price || auctionData.buy_now_price || auctionData.duration_hours)) {
          try {
            const auctionPayload = {};
            if (auctionData.start_price) auctionPayload.start_price = parseFloat(auctionData.start_price);
            if (auctionData.buy_now_price) auctionPayload.buy_now_price = parseFloat(auctionData.buy_now_price);
            if (auctionData.duration_hours) auctionPayload.duration_hours = parseInt(auctionData.duration_hours);
            await axios.put(`${API}/auctions/${editingVehicle.vehicle_id}`, auctionPayload, { headers: getHeaders(), withCredentials: true });
          } catch (auctionErr) {
            // No auction to update or auction update skipped
          }
        }
        alert('Vehicle updated successfully!');
      } else {
        const vehicleRes = await axios.post(`${API}/vehicles`, payload, { headers: getHeaders(), withCredentials: true });
        if (formData.listing_type === 'auction') {
          await axios.post(`${API}/auctions`, {
            vehicle_id: vehicleRes.data.vehicle_id,
            start_price: parseFloat(auctionData.start_price),
            buy_now_price: auctionData.buy_now_price ? parseFloat(auctionData.buy_now_price) : null,
            duration_hours: parseInt(auctionData.duration_hours)
          }, { headers: getHeaders(), withCredentials: true });
        }
        alert('Vehicle uploaded successfully!');
      }
      
      setShowUploadForm(false);
      setEditingVehicle(null);
      resetForm();
      fetchDealerData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save vehicle');
    } finally {
      setUploadingVehicle(false);
    }
  };

  const startEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      make: vehicle.make, model: vehicle.model, year: vehicle.year, vin: vehicle.vin,
      mileage: vehicle.mileage, condition: vehicle.condition, location: vehicle.location,
      body_type: vehicle.body_type, damage_type: vehicle.damage_type || '',
      description: vehicle.description, transmission: vehicle.transmission || 'Automatic',
      fuel_type: vehicle.fuel_type || 'Gasoline', color: vehicle.color || 'Black',
      drivetrain: vehicle.drivetrain || 'FWD', engine_size: vehicle.engine_size || '2.0L',
      images: vehicle.images, listing_type: vehicle.listing_type || 'auction',
      price: vehicle.price || '', currency: vehicle.currency || 'USD'
    });
    setUploadedImages(vehicle.images || []);
    if (vehicle.auction) {
      setAuctionData({
        start_price: vehicle.auction.start_price || '',
        buy_now_price: vehicle.auction.buy_now_price || '',
        duration_hours: vehicle.auction.end_time && vehicle.auction.start_time
          ? Math.round((new Date(vehicle.auction.end_time) - new Date(vehicle.auction.start_time)) / 3600000)
          : 72
      });
    } else {
      setAuctionData({ start_price: '', buy_now_price: '', duration_hours: 72 });
    }
    setShowUploadForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (vehicleId) => {
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, { headers: getHeaders(), withCredentials: true });
      alert('Vehicle deleted successfully!');
      setDeleteConfirm(null);
      fetchDealerData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete vehicle');
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setAuctionData({ start_price: '', buy_now_price: '', duration_hours: 72 });
    setUploadedImages([]);
    setEditingVehicle(null);
  };

  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload JPG, PNG, or PDF files only.');
      return;
    }
    
    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('File too large. Maximum size is 10MB.');
      return;
    }
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/dealers/upload-document?document_type=${docType}`, formData, {
        headers: getHeaders(),
        withCredentials: true
      });
      
      // Show message from backend
      alert(response.data.message || 'Document uploaded successfully!');
      
      // Refresh to get updated documents and status
      await fetchDealerData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload document. Please try again.';
      alert(errorMsg);
      console.error('Upload error:', error);
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocumentDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document? You will need to upload a replacement.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/dealers/document/${documentId}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      
      alert(response.data.message || 'Document deleted successfully!');
      await fetchDealerData(); // Refresh to get updated documents and status
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleSubmitVerification = async () => {
    // Prevent multiple submissions
    if (submittingVerification) {
      return;
    }
    
    // Count unique document types
    const uniqueTypes = new Set(verificationDocs.map(d => d.type));
    const uniqueCount = uniqueTypes.size;
    
    if (uniqueCount < 2) {
      toast.error(`You need at least 2 different documents to submit for verification. You currently have ${uniqueCount}.`);
      return;
    }
    
    // Show custom confirmation modal instead of window.confirm
    setShowSubmitConfirmModal(true);
  };
  
  const confirmSubmitVerification = async () => {
    setShowSubmitConfirmModal(false);
    
    const uniqueCount = new Set(verificationDocs.map(d => d.type)).size;
    
    setSubmittingVerification(true);
    toast.loading('Submitting verification...');
    
    try {
      const response = await axios.post(`${API}/dealers/submit-verification`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      
      toast.dismiss();
      toast.success(response.data.message || 'Verification submitted successfully! Admin will review your documents shortly.');
      
      // Refresh to get updated status
      await fetchDealerData();
      
      // Hide the verification section after successful submission
      setShowVerificationSection(false);
    } catch (error) {
      console.error('Verification submission error:', error);
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to submit verification. Please try again.');
    } finally {
      setSubmittingVerification(false);
    }
  };

  const canUploadVehicles = () => {
    // Admin users bypass all verification requirements
    if (user?.role === 'admin') {
      return true;
    }
    // Dealers must be verified
    return verificationStatus === 'verified';
  };

  // Helper to get unique document count
  const getUniqueDocCount = () => {
    const uniqueTypes = new Set(verificationDocs.map(d => d.type));
    return uniqueTypes.size;
  };

  // Helper to check if a document type is uploaded
  const getDocByType = (docType) => {
    return verificationDocs.find(d => d.type === docType);
  };


  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const inputClasses = "w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary";
  const selectClasses = inputClasses;
  const isAuction = formData.listing_type === 'auction';
  
  // Check dealer approval and verification status
  const approvalStatus = user?.approval_status || 'approved';
  const isPending = approvalStatus === 'pending';
  const isRejected = approvalStatus === 'rejected';
  const isVerified = verificationStatus === 'verified';
  const isPendingVerification = verificationStatus === 'pending_verification';
  const isVerificationRejected = verificationStatus === 'rejected';
  const isUnverified = verificationStatus === 'unverified' || !verificationStatus;

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-heading font-bold mb-2 flex items-center space-x-3" data-testid="dealer-dashboard-title">
              <span>Dealer Dashboard</span>
              {user && user.role === 'admin' && (
                <span className="flex items-center space-x-2 px-4 py-2 bg-blue-600 rounded-sm text-sm font-bold">
                  <CheckCircle className="w-5 h-5" />
                  <span>Admin Access</span>
                </span>
              )}
              {user && user.role === 'dealer' && isVerified && (
                <span className="flex items-center space-x-2 px-4 py-2 bg-green-600 rounded-sm text-sm font-bold">
                  <CheckCircle className="w-5 h-5" />
                  <span>Verified Dealer</span>
                </span>
              )}
              {user && user.role === 'dealer' && isPendingVerification && (
                <span className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 rounded-sm text-sm font-bold">
                  <Clock className="w-5 h-5" />
                  <span>Pending Verification</span>
                </span>
              )}
              {user && user.role === 'dealer' && isUnverified && (
                <span className="flex items-center space-x-2 px-4 py-2 bg-red-600/80 rounded-sm text-sm font-bold">
                  <AlertCircle className="w-5 h-5" />
                  <span>Not Verified</span>
                </span>
              )}
            </h1>
            <p className="text-textSecondary">Manage your vehicle listings and auctions</p>
          </div>
          {(isVerified || (user && user.role === 'admin')) ? (
            <button onClick={() => { resetForm(); setShowUploadForm(!showUploadForm); }} data-testid="upload-vehicle-button"
              className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 flex items-center space-x-2 transition-colors red-glow">
              <Plus className="w-5 h-5" /><span>Upload Vehicle</span>
            </button>
          ) : (
            <div className="flex items-center space-x-2 bg-yellow-900/20 border border-yellow-600 text-yellow-500 rounded-sm font-bold px-6 py-3">
              <AlertCircle className="w-5 h-5" />
              <span>Verification Required</span>
            </div>
          )}
        </div>

        {/* Pending Approval Banner */}
        {isPending && (
          <div className="bg-yellow-600/20 border-2 border-yellow-600 rounded-sm p-6 mb-6">
            <div className="flex items-start space-x-3">
              <Clock className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-heading font-bold text-yellow-500 mb-2">Account Pending Approval</h3>
                <p className="text-textSecondary mb-2">
                  Your dealer account is currently pending admin approval. You will not be able to upload or manage vehicle listings until your account is approved.
                </p>
                <p className="text-sm text-textSecondary">
                  This process typically takes 24-48 hours. You will receive an email notification once your account has been reviewed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rejected Banner */}
        {isRejected && (
          <div className="bg-red-600/20 border-2 border-red-600 rounded-sm p-6 mb-6">
            <div className="flex items-start space-x-3">
              <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-heading font-bold text-red-500 mb-2">Account Rejected</h3>
                <p className="text-textSecondary mb-2">
                  Your dealer account application has been rejected. You cannot upload or manage vehicle listings at this time.
                </p>
                <p className="text-sm text-textSecondary">
                  Please contact support at admin@jamohautos.com for more information.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Contact Information Section */}
        <div className="bg-surfaceHighlight border border-border rounded-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-heading font-bold text-textPrimary">Contact Information</h3>
              <p className="text-sm text-textSecondary mt-1">
                Manage your contact details for buyer communication
              </p>
            </div>
            {!editingContact ? (
              <button
                onClick={() => setEditingContact(true)}
                className="bg-primary hover:bg-primaryHover text-white px-4 py-2 rounded-sm font-medium transition-colors flex items-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setEditingContact(false);
                    // Reset to original values
                    fetchDealerData();
                  }}
                  disabled={savingContact}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-sm font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveContactInfo}
                  disabled={savingContact}
                  className="bg-primary hover:bg-primaryHover text-white px-4 py-2 rounded-sm font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {savingContact ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Save</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              {editingContact ? (
                <input
                  type="tel"
                  value={contactInfo.phone}
                  onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                  placeholder="+234801234567..."
                  className="w-full bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary focus:outline-none focus:border-primary"
                />
              ) : (
                <div className="bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary min-h-[42px] flex items-center">
                  {contactInfo.phone || <span className="text-textSecondary">Not set</span>}
                </div>
              )}
              <p className="text-xs text-textSecondary mt-1">
                Format: +234... (with country code)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                WhatsApp Number
              </label>
              {editingContact ? (
                <input
                  type="tel"
                  value={contactInfo.whatsapp}
                  onChange={(e) => setContactInfo({ ...contactInfo, whatsapp: e.target.value })}
                  placeholder="+234801234567... or wa.me link"
                  className="w-full bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary focus:outline-none focus:border-primary"
                />
              ) : (
                <div className="bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary min-h-[42px] flex items-center">
                  {contactInfo.whatsapp || <span className="text-textSecondary">Not set</span>}
                </div>
              )}
              <p className="text-xs text-textSecondary mt-1">
                Optional: Can be same as phone
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              {editingContact ? (
                <input
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary focus:outline-none focus:border-primary"
                />
              ) : (
                <div className="bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary min-h-[42px] flex items-center">
                  {contactInfo.email || <span className="text-textSecondary">Not set</span>}
                </div>
              )}
              <p className="text-xs text-textSecondary mt-1">
                For buyer communication
              </p>
            </div>
          </div>

          {!contactInfo.phone && !editingContact && (
            <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-600 rounded-sm">
              <p className="text-xs text-yellow-500 flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                Please add your contact information so buyers can reach you after making a reservation.
              </p>
            </div>
          )}
        </div>

        {/* Bank & Payout Section (Paystack Subaccount) */}
        <div className="bg-surfaceHighlight border border-border rounded-sm p-6 mb-6" data-testid="bank-payout-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-heading font-bold text-textPrimary">Bank & Payout Details</h3>
              <p className="text-sm text-textSecondary mt-1">
                Buyers pay JAMOH; Paystack automatically splits each payment so your share is settled directly to your bank account. JAMOH never holds your funds.
              </p>
            </div>
            {!editingBank ? (
              <button
                onClick={() => { setEditingBank(true); setBankFormError(''); }}
                data-testid="bank-edit-button"
                className="bg-primary hover:bg-primaryHover text-white px-4 py-2 rounded-sm font-medium transition-colors flex items-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>{bankDetails.account_number ? 'Edit' : 'Add Bank'}</span>
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={() => { setEditingBank(false); setBankFormError(''); fetchDealerData(); }}
                  disabled={savingBank}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-sm font-medium transition-colors disabled:opacity-50"
                >Cancel</button>
                <button
                  onClick={handleSaveBankDetails}
                  disabled={savingBank}
                  data-testid="bank-save-button"
                  className="bg-primary hover:bg-primaryHover text-white px-4 py-2 rounded-sm font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {savingBank ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div><span>Saving...</span></>
                  ) : (
                    <><Save className="w-4 h-4" /><span>Save</span></>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Subaccount status banner */}
          <div className={`mb-4 p-3 rounded-sm border ${bankDetails.has_subaccount ? 'bg-green-900/20 border-green-600 text-green-400' : (bankDetails.account_number ? 'bg-yellow-900/20 border-yellow-600 text-yellow-400' : 'bg-red-900/20 border-red-600 text-red-400')}`}>
            <p className="text-xs flex items-center" data-testid="subaccount-status">
              {bankDetails.has_subaccount ? (
                <><CheckCircle className="w-4 h-4 mr-2" /><span>Payout subaccount active: <span className="font-mono">{bankDetails.paystack_subaccount_code}</span> — you will receive your share of every sale directly.</span></>
              ) : bankDetails.account_number ? (
                <><Clock className="w-4 h-4 mr-2" /><span>Bank details saved. Awaiting JAMOH admin to provision your payout subaccount. Buyers cannot reserve your vehicles until this is complete.</span></>
              ) : (
                <><AlertCircle className="w-4 h-4 mr-2" /><span>You haven&apos;t added bank details yet. Buyers cannot reserve your vehicles until your payout subaccount is set up.</span></>
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Business Name <span className="text-red-500">*</span></label>
              {editingBank ? (
                <input
                  type="text"
                  value={bankDetails.business_name}
                  onChange={(e) => setBankDetails({ ...bankDetails, business_name: e.target.value })}
                  placeholder="As registered with your bank"
                  data-testid="bank-business-name-input"
                  className="w-full bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary focus:outline-none focus:border-primary"
                />
              ) : (
                <div className="bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary min-h-[42px] flex items-center">
                  {bankDetails.business_name || <span className="text-textSecondary">Not set</span>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Bank <span className="text-red-500">*</span></label>
              {editingBank ? (
                <select
                  value={bankDetails.bank_code}
                  onChange={handleBankSelect}
                  data-testid="bank-select"
                  className="w-full bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary focus:outline-none focus:border-primary"
                >
                  <option value="">Select your bank...</option>
                  {banks.map((b) => (
                    <option key={b.code} value={b.code}>{b.name}</option>
                  ))}
                </select>
              ) : (
                <div className="bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary min-h-[42px] flex items-center">
                  {bankDetails.bank_name || <span className="text-textSecondary">Not set</span>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Account Number <span className="text-red-500">*</span></label>
              {editingBank ? (
                <div className="flex space-x-2">
                  <input
                    type="text"
                    maxLength={10}
                    value={bankDetails.account_number}
                    onChange={(e) => { setBankDetails({ ...bankDetails, account_number: e.target.value.replace(/\D/g, '') }); setResolvedAccountName(''); }}
                    placeholder="10-digit NUBAN"
                    data-testid="bank-account-number-input"
                    className="flex-1 bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary focus:outline-none focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={handleResolveAccount}
                    disabled={resolvingAccount}
                    data-testid="bank-verify-button"
                    className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-sm font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
                  >{resolvingAccount ? 'Verifying...' : 'Verify'}</button>
                </div>
              ) : (
                <div className="bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary min-h-[42px] flex items-center font-mono">
                  {bankDetails.account_number || <span className="text-textSecondary">Not set</span>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Account Name (verified)</label>
              <div className="bg-surface border border-border rounded-sm px-4 py-2 text-textPrimary min-h-[42px] flex items-center" data-testid="bank-account-name-display">
                {(editingBank ? resolvedAccountName : bankDetails.account_name) || <span className="text-textSecondary">Click Verify to confirm</span>}
              </div>
            </div>
          </div>

          {bankFormError && (
            <div className="mt-3 p-3 bg-red-900/20 border border-red-600 rounded-sm">
              <p className="text-xs text-red-400 flex items-center"><AlertCircle className="w-4 h-4 mr-2" />{bankFormError}</p>
            </div>
          )}
        </div>

        {/* ADMIN INFO BOX - Admins don't need dealer verification */}
        {user && user.role === 'admin' && (
          <div className="glass p-8 rounded-sm mb-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2 text-blue-400">Admin Account - Full Access</h3>
                <p className="text-textSecondary mb-4">
                  As an admin, you have automatic verification and full platform privileges. Admin accounts do not require dealer verification.
                </p>
                <div className="flex items-center space-x-2 text-sm">
                  <span className="px-3 py-1 bg-blue-600 rounded-sm font-bold uppercase">Admin Verified</span>
                  <span className="text-green-400">✓ All features unlocked</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Verification Section - Only show for dealers */}
        {user && user.role === 'dealer' && !isVerified && !isRejected && !isPending && (
          <div className={`${isUnverified ? 'bg-red-600/20 border-red-600' : isPendingVerification ? 'bg-yellow-600/20 border-yellow-600' : 'bg-blue-600/20 border-blue-600'} border-2 rounded-sm p-6 mb-6`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                {isUnverified && <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />}
                {isPendingVerification && <Clock className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />}
                {isVerificationRejected && <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />}
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-bold mb-2">
                    {isUnverified && 'Verification Required'}
                    {isPendingVerification && 'Verification Under Review'}
                    {isVerificationRejected && 'Verification Rejected'}
                  </h3>
                  <p className="text-textSecondary mb-3">
                    {isUnverified && 'You must upload verification documents before you can list vehicles. Upload at least 2 documents to proceed.'}
                    {isPendingVerification && 'Your verification documents are being reviewed by our admin team. You will be notified once your account is verified.'}
                    {isVerificationRejected && 'Your verification was rejected. Please review the reason below, upload corrected documents, and resubmit.'}
                  </p>
                  
                  {/* Show rejection reason */}
                  {isVerificationRejected && rejectionReason && (
                    <div className="mb-4 p-4 bg-red-900/30 border border-red-600 rounded-sm">
                      <p className="text-sm font-bold text-red-400 mb-1">Rejection Reason:</p>
                      <p className="text-sm text-white">{rejectionReason}</p>
                    </div>
                  )}
                  
                  {(isUnverified || isVerificationRejected) && (
                    <>
                      <button
                        onClick={() => setShowVerificationSection(!showVerificationSection)}
                        className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 mb-4 transition-colors"
                      >
                        <Upload className="w-5 h-5 inline mr-2" />
                        {showVerificationSection ? 'Hide' : 'Upload'} Verification Documents
                      </button>

                      {showVerificationSection && (
                        <div className="mt-4 glass p-6 rounded-sm">
                          <h4 className="font-heading font-bold text-lg mb-4">Upload Documents (Minimum 2 Required)</h4>
                          
                          {/* Show currently uploaded documents with preview and delete */}
                          {verificationDocs.length > 0 && (
                            <div className="mb-6 p-4 bg-surfaceHighlight rounded-sm border border-white/20">
                              <p className="text-sm font-bold mb-3">Currently Uploaded Documents:</p>
                              <div className="space-y-2">
                                {verificationDocs.map((doc) => (
                                  <div key={doc.document_id} className="flex items-center justify-between bg-surface p-3 rounded-sm">
                                    <div className="flex items-center space-x-3">
                                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                      <div>
                                        <p className="text-sm font-medium">{doc.type.replace(/_/g, ' ').toUpperCase()}</p>
                                        <p className="text-xs text-textSecondary">{doc.original_filename}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/dealer_documents/${doc.filename}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:text-primaryHover transition-colors px-3 py-1 text-sm border border-primary rounded-sm"
                                      >
                                        <Eye className="w-4 h-4 inline mr-1" />
                                        View
                                      </a>
                                      <button
                                        onClick={() => handleDocumentDelete(doc.document_id)}
                                        className="text-red-500 hover:text-red-400 transition-colors px-3 py-1 text-sm border border-red-500 rounded-sm"
                                      >
                                        <Trash2 className="w-4 h-4 inline mr-1" />
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {/* Dealer License */}
                            <div className="border border-white/20 rounded-sm p-4">
                              <label className="block text-sm font-bold mb-2 flex items-center space-x-2">
                                <FileText className="w-4 h-4 text-primary" />
                                <span>Dealer License</span>
                              </label>
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => e.target.files[0] && handleDocumentUpload('dealer_license', e.target.files[0])}
                                disabled={uploadingDoc}
                                className="w-full text-sm"
                              />
                              {getDocByType('dealer_license') && (
                                <p className="text-xs text-green-500 mt-2 flex items-center">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Uploaded (replacing will update)
                                </p>
                              )}
                            </div>

                            {/* Dealer ID */}
                            <div className="border border-white/20 rounded-sm p-4">
                              <label className="block text-sm font-bold mb-2 flex items-center space-x-2">
                                <FileText className="w-4 h-4 text-primary" />
                                <span>Dealer ID Card</span>
                              </label>
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => e.target.files[0] && handleDocumentUpload('dealer_id', e.target.files[0])}
                                disabled={uploadingDoc}
                                className="w-full text-sm"
                              />
                              {getDocByType('dealer_id') && (
                                <p className="text-xs text-green-500 mt-2 flex items-center">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Uploaded (replacing will update)
                                </p>
                              )}
                            </div>

                            {/* CAC Certificate */}
                            <div className="border border-white/20 rounded-sm p-4">
                              <label className="block text-sm font-bold mb-2 flex items-center space-x-2">
                                <FileText className="w-4 h-4 text-primary" />
                                <span>CAC Certificate</span>
                              </label>
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => e.target.files[0] && handleDocumentUpload('cac_certificate', e.target.files[0])}
                                disabled={uploadingDoc}
                                className="w-full text-sm"
                              />
                              {getDocByType('cac_certificate') && (
                                <p className="text-xs text-green-500 mt-2 flex items-center">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Uploaded (replacing will update)
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="bg-surfaceHighlight p-4 rounded-sm">
                            <p className="text-sm font-bold mb-2">
                              Documents Uploaded: {getUniqueDocCount()} / 2 minimum (max 3 slots)
                            </p>
                            <p className="text-xs text-textSecondary">
                              Accepted formats: PDF, JPG, JPEG, PNG. Each file must be under 10MB.
                            </p>
                            {getUniqueDocCount() < 2 && (
                              <p className="text-xs text-yellow-500 mt-2">
                                ⚠️ You need to upload at least 2 different documents before submitting for verification.
                              </p>
                            )}
                            {getUniqueDocCount() >= 2 && (isUnverified || isVerificationRejected) && (
                              <div className="mt-3 p-3 bg-green-900/20 border border-green-600 rounded-sm">
                                <p className="text-xs text-green-500 mb-3 flex items-center">
                                  <CheckCircle className="w-4 h-4 mr-1" /> You have {getUniqueDocCount()} document type(s) ready. Review your uploads and {isVerificationRejected ? 'resubmit' : 'submit'} when ready.
                                </p>
                                <button
                                  onClick={handleSubmitVerification}
                                  disabled={submittingVerification}
                                  className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 flex items-center justify-center space-x-2 transition-colors red-glow disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {submittingVerification ? (
                                    <>
                                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      <span>Submitting...</span>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="w-5 h-5" />
                                      <span>{isVerificationRejected ? 'Resubmit' : 'Submit'} Verification for Admin Review</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                          </div>

                          {uploadingDoc && (
                            <div className="mt-4 flex items-center space-x-2 text-primary">
                              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                              <span>Uploading document...</span>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {isPendingVerification && verificationDocs.length > 0 && (
                    <div className="mt-4 glass p-4 rounded-sm">
                      <p className="text-sm font-bold mb-2">Uploaded Documents:</p>
                      <ul className="text-sm space-y-1">
                        {verificationDocs.map((doc, idx) => (
                          <li key={idx} className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span>{doc.type.replace(/_/g, ' ').toUpperCase()}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-12">
          <div className="glass p-6 rounded-sm">
            <Package className="w-8 h-8 text-primary mb-4" />
            <div className="text-3xl font-heading font-bold mb-1">{(vehicles || []).length}</div>
            <div className="text-sm text-textSecondary">Total Listings</div>
          </div>
          <div className="glass p-6 rounded-sm">
            <Clock className="w-8 h-8 text-primary mb-4" />
            <div className="text-3xl font-heading font-bold mb-1">{(vehicles || []).filter(v => v.listing_type !== 'direct_sale' && v.auction?.status === 'active').length}</div>
            <div className="text-sm text-textSecondary">Active Auctions</div>
          </div>
          <div className="glass p-6 rounded-sm">
            <Tag className="w-8 h-8 text-primary mb-4" />
            <div className="text-3xl font-heading font-bold mb-1">{(vehicles || []).filter(v => v.listing_type === 'direct_sale').length}</div>
            <div className="text-sm text-textSecondary">Direct Sales</div>
          </div>
          <div className="glass p-6 rounded-sm">
            <DollarSign className="w-8 h-8 text-green-500 mb-4" />
            <div className="text-3xl font-heading font-bold mb-1 text-green-500">
              ₦{(payments || []).filter(p => p.status === 'successful').reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
            </div>
            <div className="text-sm text-textSecondary">Total Deposits</div>
          </div>
          <div className="glass p-6 rounded-sm">
            <DollarSign className="w-8 h-8 text-primary mb-4" />
            <div className="text-3xl font-heading font-bold mb-1">{(payments || []).filter(p => p.status === 'successful').length}</div>
            <div className="text-sm text-textSecondary">Paid Reservations</div>
          </div>
        </div>

        {/* Upload/Edit Form */}
        {showUploadForm && (
          <div className="glass p-8 rounded-sm mb-12 animate-fadeIn">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-bold">{editingVehicle ? 'Edit Vehicle' : 'Upload New Vehicle'}</h2>
              <button onClick={() => { setShowUploadForm(false); resetForm(); }} className="text-textSecondary hover:text-white"><X className="w-6 h-6" /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Listing Type */}
              <div className="border-b border-white/10 pb-6">
                <h3 className="text-xl font-heading font-bold mb-4">Listing Type</h3>
                <div className="grid grid-cols-2 gap-4">
                  <button type="button" onClick={() => setFormData({...formData, listing_type: 'auction'})} data-testid="listing-type-auction"
                    className={`p-4 rounded-sm border-2 transition-all text-left ${isAuction ? 'border-primary bg-primary/10' : 'border-white/10 bg-surfaceHighlight hover:border-white/30'}`}>
                    <Clock className={`w-6 h-6 mb-2 ${isAuction ? 'text-primary' : 'text-textSecondary'}`} />
                    <div className="font-heading font-bold">Auction Listing</div>
                    <div className="text-xs text-textSecondary mt-1">Buyers bid competitively. Set starting price & duration.</div>
                  </button>
                  <button type="button" onClick={() => setFormData({...formData, listing_type: 'direct_sale'})} data-testid="listing-type-direct"
                    className={`p-4 rounded-sm border-2 transition-all text-left ${!isAuction ? 'border-primary bg-primary/10' : 'border-white/10 bg-surfaceHighlight hover:border-white/30'}`}>
                    <Tag className={`w-6 h-6 mb-2 ${!isAuction ? 'text-primary' : 'text-textSecondary'}`} />
                    <div className="font-heading font-bold">Direct Sale</div>
                    <div className="text-xs text-textSecondary mt-1">Fixed price. Buyers purchase directly at listed price.</div>
                  </button>
                </div>
              </div>

              {/* Basic Info */}
              <div className="border-b border-white/10 pb-6">
                <h3 className="text-xl font-heading font-bold mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><label className="block text-sm font-medium mb-2">Make *</label>
                    <input type="text" name="make" value={formData.make} onChange={handleInputChange} required className={inputClasses} placeholder="e.g., Toyota" /></div>
                  <div><label className="block text-sm font-medium mb-2">Model *</label>
                    <input type="text" name="model" value={formData.model} onChange={handleInputChange} required className={inputClasses} placeholder="e.g., Camry" /></div>
                  <div><label className="block text-sm font-medium mb-2">Year *</label>
                    <input type="number" name="year" value={formData.year} onChange={handleInputChange} required min="2000" max={new Date().getFullYear() + 1} className={inputClasses} /></div>
                  <div><label className="block text-sm font-medium mb-2">VIN Number *</label>
                    <input type="text" name="vin" value={formData.vin} onChange={handleInputChange} required className={inputClasses} placeholder="17-character VIN" /></div>
                  <div><label className="block text-sm font-medium mb-2">Mileage *</label>
                    <input type="number" name="mileage" value={formData.mileage} onChange={handleInputChange} required className={inputClasses} placeholder="Miles" /></div>
                  <div><label className="block text-sm font-medium mb-2">Location *</label>
                    <input type="text" name="location" value={formData.location} onChange={handleInputChange} required className={inputClasses} placeholder="City, State" /></div>
                </div>
              </div>

              {/* Vehicle Details */}
              <div className="border-b border-white/10 pb-6">
                <h3 className="text-xl font-heading font-bold mb-4">Vehicle Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><label className="block text-sm font-medium mb-2">Condition *</label>
                    <select name="condition" value={formData.condition} onChange={handleInputChange} className={selectClasses}>
                      <option value="clean">Clean</option><option value="salvage">Salvage</option><option value="damaged">Damaged</option>
                    </select></div>
                  <div><label className="block text-sm font-medium mb-2">Body Type *</label>
                    <select name="body_type" value={formData.body_type} onChange={handleInputChange} className={selectClasses}>
                      <option value="sedan">Sedan</option><option value="suv">SUV</option><option value="truck">Truck</option>
                      <option value="luxury">Luxury</option><option value="electric">Electric</option>
                    </select></div>
                  <div><label className="block text-sm font-medium mb-2">Transmission</label>
                    <select name="transmission" value={formData.transmission} onChange={handleInputChange} className={selectClasses}>
                      <option value="Automatic">Automatic</option><option value="Manual">Manual</option><option value="CVT">CVT</option>
                    </select></div>
                  <div><label className="block text-sm font-medium mb-2">Fuel Type</label>
                    <select name="fuel_type" value={formData.fuel_type} onChange={handleInputChange} className={selectClasses}>
                      <option value="Gasoline">Gasoline</option><option value="Diesel">Diesel</option><option value="Electric">Electric</option><option value="Hybrid">Hybrid</option>
                    </select></div>
                  <div><label className="block text-sm font-medium mb-2">Color</label>
                    <select name="color" value={formData.color} onChange={handleInputChange} className={selectClasses}>
                      <option value="Black">Black</option><option value="White">White</option><option value="Silver">Silver</option>
                      <option value="Gray">Gray</option><option value="Red">Red</option><option value="Blue">Blue</option>
                    </select></div>
                  <div><label className="block text-sm font-medium mb-2">Drivetrain</label>
                    <select name="drivetrain" value={formData.drivetrain} onChange={handleInputChange} className={selectClasses}>
                      <option value="FWD">FWD</option><option value="RWD">RWD</option><option value="AWD">AWD</option><option value="4WD">4WD</option>
                    </select></div>
                  <div><label className="block text-sm font-medium mb-2">Engine Size</label>
                    <input
                      type="text"
                      name="engine_size"
                      value={formData.engine_size}
                      onChange={handleInputChange}
                      list="engine-size-options"
                      className={inputClasses}
                      placeholder="e.g., 2.0L, 3.5L, Electric"
                    />
                    <datalist id="engine-size-options">
                      <option value="1.5L" />
                      <option value="1.8L" />
                      <option value="2.0L" />
                      <option value="2.4L" />
                      <option value="2.5L" />
                      <option value="2.7L" />
                      <option value="3.0L" />
                      <option value="3.5L" />
                      <option value="5.0L" />
                      <option value="6.2L" />
                      <option value="Hybrid" />
                      <option value="Electric" />
                    </datalist>
                  </div>
                  {formData.condition !== 'clean' && (
                    <div><label className="block text-sm font-medium mb-2">Damage Type</label>
                      <input type="text" name="damage_type" value={formData.damage_type} onChange={handleInputChange} className={inputClasses} placeholder="e.g., Front End" /></div>
                  )}
                </div>
              </div>

              {/* Image Upload */}
              <div className="border-b border-white/10 pb-6">
                <h3 className="text-xl font-heading font-bold mb-4">Vehicle Images</h3>
                <div className="mb-4">
                  <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/jpeg,image/png,image/webp" multiple className="hidden" data-testid="image-file-input" />
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingImages}
                    className="bg-surfaceHighlight hover:bg-white/10 border border-dashed border-white/30 text-white rounded-sm font-medium px-6 py-4 flex items-center space-x-3 transition-colors w-full justify-center"
                    data-testid="upload-images-button">
                    {uploadingImages ? (
                      <><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div><span>Uploading...</span></>
                    ) : (
                      <><Upload className="w-5 h-5" /><span>Click to upload images (JPEG, PNG, WebP)</span></>
                    )}
                  </button>
                </div>
                {uploadedImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {uploadedImages.map((url, i) => (
                      <div key={i} className="relative group">
                        <img src={resolveImg(url)} alt={`Upload ${i+1}`} className="w-full aspect-video object-cover rounded-sm" />
                        <button type="button" onClick={() => removeImage(i)}
                          className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {uploadedImages.length === 0 && <p className="text-sm text-textSecondary">At least 1 image required</p>}
              </div>

              {/* Description */}
              <div className="border-b border-white/10 pb-6">
                <h3 className="text-xl font-heading font-bold mb-4">Description</h3>
                <textarea name="description" value={formData.description} onChange={handleInputChange} required rows="4" className={inputClasses} placeholder="Detailed description of the vehicle..."></textarea>
              </div>

              {/* Pricing & Auction Settings */}
              <div>
                <h3 className="text-xl font-heading font-bold mb-4">
                  {isAuction ? 'Auction Settings' : 'Pricing'}
                </h3>
                {/* Currency Selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Currency *</label>
                  <div className="flex gap-3" data-testid="currency-toggle">
                    <label className={`flex-1 cursor-pointer px-4 py-3 border rounded-sm font-bold text-center transition-colors ${formData.currency === 'USD' ? 'border-primary bg-primary/10 text-primary' : 'border-white/10 text-textSecondary hover:border-white/30'}`}>
                      <input type="radio" name="currency" value="USD" checked={formData.currency === 'USD'} onChange={handleInputChange} className="sr-only" data-testid="currency-usd" />
                      USD ($) - International
                    </label>
                    <label className={`flex-1 cursor-pointer px-4 py-3 border rounded-sm font-bold text-center transition-colors ${formData.currency === 'NGN' ? 'border-primary bg-primary/10 text-primary' : 'border-white/10 text-textSecondary hover:border-white/30'}`}>
                      <input type="radio" name="currency" value="NGN" checked={formData.currency === 'NGN'} onChange={handleInputChange} className="sr-only" data-testid="currency-ngn" />
                      NGN (₦) - Nigeria Local
                    </label>
                  </div>
                  <p className="text-xs text-textSecondary mt-2">
                    {formData.currency === 'NGN'
                      ? 'Prices entered will be charged exactly in Naira. Buyers pay this amount with no conversion.'
                      : 'Prices entered in USD. The platform will display an approximate NGN equivalent using the current exchange rate.'}
                  </p>
                </div>
                {isAuction ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><label className="block text-sm font-medium mb-2">Starting Price ({formData.currency === 'NGN' ? '₦' : '$'}) *</label>
                      <input type="number" name="start_price" value={auctionData.start_price} onChange={handleAuctionChange} required min="0" step="any" className={inputClasses} placeholder="e.g., 150" data-testid="start-price-input" /></div>
                    <div><label className="block text-sm font-medium mb-2">Buy Now Price ({formData.currency === 'NGN' ? '₦' : '$'})</label>
                      <input type="number" name="buy_now_price" value={auctionData.buy_now_price} onChange={handleAuctionChange} min="0" step="any" className={inputClasses} placeholder="Optional" data-testid="buy-now-price-input" /></div>
                    <div><label className="block text-sm font-medium mb-2">Auction Duration *</label>
                      <select name="duration_hours" value={auctionData.duration_hours} onChange={handleAuctionChange} className={selectClasses} data-testid="duration-select">
                        <option value="12">12h (Half day)</option><option value="24">24h (1 day)</option><option value="48">48h (2 days)</option>
                        <option value="72">72h (3 days)</option><option value="120">120h (5 days)</option><option value="168">168h (7 days)</option>
                      </select></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium mb-2">Sale Price ({formData.currency === 'NGN' ? '₦' : '$'}) *</label>
                      <input type="number" name="price" value={formData.price} onChange={handleInputChange} required min="0" step="any" className={inputClasses} placeholder={formData.currency === 'NGN' ? 'e.g., 15000' : 'e.g., 15500'} data-testid="direct-price-input" /></div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-4 pt-6">
                <button type="button" onClick={() => { setShowUploadForm(false); resetForm(); }}
                  className="bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-sm font-bold px-6 py-3 transition-colors">Cancel</button>
                <button type="submit" disabled={uploadingVehicle} data-testid="submit-vehicle-button"
                  className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-8 py-3 transition-all duration-300 red-glow disabled:opacity-50 flex items-center space-x-2">
                  {editingVehicle ? <><Save className="w-5 h-5" /><span>{uploadingVehicle ? 'Saving...' : 'Save Changes'}</span></> 
                    : <><Upload className="w-5 h-5" /><span>{uploadingVehicle ? 'Uploading...' : 'Upload Vehicle'}</span></>}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* My Vehicles */}
        <div>
          <h2 className="text-2xl font-heading font-bold mb-6">My Vehicles</h2>
          
          {/* Verification Required Message - Hide for Admin */}
          {!canUploadVehicles() && user?.role !== 'admin' && (
            <div className="glass p-8 rounded-sm mb-6 text-center border border-yellow-600">
              <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-xl font-heading font-bold mb-2">Verification Required</h3>
              <p className="text-textSecondary mb-4">
                Complete dealer verification to list vehicles on the platform.
              </p>
              {isUnverified && (
                <button 
                  onClick={() => setShowVerificationSection(true)}
                  className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 inline-block transition-colors"
                >
                  Start Verification
                </button>
              )}
              {isPendingVerification && (
                <p className="text-yellow-500 font-medium">Your verification is pending admin approval.</p>
              )}
              {isVerificationRejected && (
                <button 
                  onClick={() => setShowVerificationSection(true)}
                  className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 inline-block transition-colors"
                >
                  Resubmit Verification
                </button>
              )}
            </div>
          )}
          
          {canUploadVehicles() && (vehicles || []).length === 0 ? (
            <div className="glass p-12 rounded-sm text-center">
              <Image className="w-16 h-16 text-textSecondary mx-auto mb-4" />
              <p className="text-textSecondary mb-4">You haven't uploaded any vehicles yet</p>
              <button onClick={() => setShowUploadForm(true)}
                className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 inline-block transition-colors">
                Upload Your First Vehicle
              </button>
            </div>
          ) : canUploadVehicles() && (vehicles || []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(vehicles || []).map((vehicle) => {
                const isDirect = vehicle.listing_type === 'direct_sale';
                const activeBoost = (boosts || []).find(b => b.vehicle_id === vehicle.vehicle_id && b.is_active);
                return (
                  <div key={vehicle.vehicle_id} className="glass rounded-sm overflow-hidden" data-testid={`vehicle-card-${vehicle.vehicle_id}`}>
                    <div className="relative">
                      <img src={resolveImg(vehicle.images[0])}
                        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`} className="w-full aspect-video object-cover" />
                      <span className={`absolute top-3 left-3 px-3 py-1 rounded-sm text-xs font-bold uppercase ${isDirect ? 'bg-blue-600' : 'bg-primary'}`}>
                        {isDirect ? 'For Sale' : 'Auction'}
                      </span>
                      {activeBoost && (
                        <span className="absolute top-3 right-3 px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-sm text-xs font-bold uppercase flex items-center space-x-1">
                          <Zap className="w-3 h-3" />
                          <span>Featured</span>
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-bold text-lg mb-2">{vehicle.year} {vehicle.make} {vehicle.model}</h3>
                      <div className="flex items-center justify-between mb-3">
                        <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase ${
                          vehicle.condition === 'clean' ? 'bg-green-600' : vehicle.condition === 'salvage' ? 'bg-yellow-600' : 'bg-red-600'
                        }`}>{vehicle.condition}</span>
                        <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase ${
                          vehicle.status === 'active' ? 'bg-blue-600' : vehicle.status === 'sold' ? 'bg-green-600' : 'bg-gray-600'
                        }`}>{vehicle.status}</span>
                      </div>
                      {isDirect ? (
                        <div className="mb-3">
                          <div className="text-sm text-textSecondary">Sale Price</div>
                          <div className="text-2xl font-heading font-bold text-primary">{vehicle.currency === 'NGN' ? '₦' : '$'}{vehicle.price?.toLocaleString()}</div>
                        </div>
                      ) : vehicle.auction ? (
                        <div className="mb-3">
                          <div className="text-sm text-textSecondary">Current Bid</div>
                          <div className="text-2xl font-heading font-bold text-primary">${vehicle.auction.current_bid?.toLocaleString()}</div>
                          <div className="text-xs text-textSecondary mt-1">{vehicle.auction.bid_count} bids</div>
                        </div>
                      ) : null}
                      <div className="flex items-center space-x-2">
                        <a href={`/vehicle/${vehicle.vehicle_id}`} data-testid={`view-vehicle-${vehicle.vehicle_id}`}
                          className="flex-1 bg-surfaceHighlight hover:bg-white/10 text-white rounded-sm font-medium px-3 py-2 text-center transition-colors flex items-center justify-center space-x-1">
                          <Eye className="w-4 h-4" /><span>View</span>
                        </a>
                        <button onClick={() => startEdit(vehicle)} data-testid={`edit-vehicle-${vehicle.vehicle_id}`}
                          className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-medium px-3 py-2 transition-colors flex items-center space-x-1">
                          <Edit className="w-4 h-4" /><span>Edit</span>
                        </button>
                        {deleteConfirm === vehicle.vehicle_id ? (
                          <div className="flex items-center space-x-1">
                            <button onClick={() => handleDelete(vehicle.vehicle_id)} data-testid={`confirm-delete-${vehicle.vehicle_id}`}
                              className="bg-red-600 hover:bg-red-700 text-white rounded-sm font-medium px-3 py-2 text-xs transition-colors">Yes</button>
                            <button onClick={() => setDeleteConfirm(null)}
                              className="bg-gray-600 hover:bg-gray-700 text-white rounded-sm font-medium px-3 py-2 text-xs transition-colors">No</button>
                          </div>
                        ) : (
                          <button onClick={() => setDeleteConfirm(vehicle.vehicle_id)} data-testid={`delete-vehicle-${vehicle.vehicle_id}`}
                            className="bg-red-600/80 hover:bg-red-600 text-white rounded-sm font-medium px-3 py-2 transition-colors flex items-center space-x-1">
                            <Trash2 className="w-4 h-4" /><span>Delete</span>
                          </button>
                        )}
                      </div>
                      {/* Boost Status or Boost Button */}
                      {activeBoost ? (
                        <div className="mt-3 px-3 py-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/50 rounded-sm flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Zap className="w-4 h-4 text-yellow-500" />
                            <span className="text-xs font-bold text-yellow-500">BOOSTED</span>
                          </div>
                          <span className="text-xs text-textSecondary">
                            Until {new Date(activeBoost.end_date).toLocaleDateString()}
                          </span>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setBoostingVehicle(vehicle);
                            setShowBoostModal(true);
                          }}
                          className="mt-3 w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-sm font-bold px-3 py-2 transition-all flex items-center justify-center space-x-2"
                        >
                          <Zap className="w-4 h-4" />
                          <span>Boost Listing</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* Car Requests Section */}
        {!isPending && !isRejected && (
          <div className="mt-12">
            <h2 className="text-2xl font-heading font-bold mb-6">Available Car Requests</h2>
            {(carRequests || []).length === 0 ? (
              <div className="glass p-8 text-center text-textSecondary">
                <p>No car requests available at this time.</p>
              </div>
            ) : (
              <div className="space-y-4">
              {(carRequests || []).map(request => {
                const isAssignedToMe = request.assigned_dealer_id === user?.user_id;
                const isAssignedToOther = request.assigned_dealer_id && !isAssignedToMe;
                const canUpdate = isAssignedToMe || !request.assigned_dealer_id;
                return (
                  <div key={request.request_id} className={`glass p-6 rounded-sm ${isAssignedToOther ? "opacity-60" : ""}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-heading font-bold text-xl">{request.make} {request.model} ({request.year_min} - {request.year_max})</h3>
                          {isAssignedToMe && <span className="px-3 py-1 bg-blue-600 rounded-sm text-xs font-bold">YOUR REQUEST</span>}
                          {isAssignedToOther && <span className="px-3 py-1 bg-gray-600 rounded-sm text-xs font-bold">ASSIGNED</span>}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mb-3">
                          <div><span className="text-textSecondary">Budget:</span> <span className="ml-2 font-bold text-primary">${request.budget?.toLocaleString()}</span></div>
                          <div><span className="text-textSecondary">Location:</span> <span className="ml-2">{request.location}</span></div>
                          <div><span className="text-textSecondary">Submitted:</span> <span className="ml-2">{new Date(request.created_at).toLocaleDateString()}</span></div>
                          {request.assigned_dealer_name && <div><span className="text-textSecondary">Handled by:</span> <span className="ml-2 font-bold text-blue-400">{request.assigned_dealer_name}</span></div>}
                        </div>
                        {request.additional_notes && <div className="mt-3 pt-3 border-t border-white/10"><p className="text-sm text-textSecondary"><strong>Notes:</strong> {request.additional_notes}</p></div>}
                      </div>
                      <div className="ml-4 flex flex-col space-y-2">
                        <span className={`px-4 py-2 rounded-sm text-sm font-bold uppercase ${request.status === "pending" ? "bg-yellow-600" : "bg-blue-600"}`}>{request.status}</span>
                        {canUpdate ? (<>{request.status === "pending" && <button onClick={() => updateRequestStatus(request.request_id, "contacted")} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-xs font-bold transition-colors">Mark Contacted</button>}<button onClick={() => updateRequestStatus(request.request_id, "fulfilled")} className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-sm text-xs font-bold transition-colors">Mark Fulfilled</button></>) : <p className="text-xs text-textSecondary text-center mt-2">Read-only</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            )}
          </div>
        )}

        {/* Vehicle Reservations */}
        {!isPending && !isRejected && (
          <div className="mt-12">
            <h2 className="text-2xl font-heading font-bold mb-6">Vehicle Reservations</h2>
            {(reservations || []).length === 0 ? (
              <div className="glass p-8 text-center text-textSecondary">
                No reservations for your vehicles yet
              </div>
            ) : (
              <div className="space-y-4">
                {(reservations || []).map((reservation) => {
                  const getStatusColor = (status) => {
                    switch(status) {
                      case 'pending': return 'bg-yellow-600';
                      case 'approved': return 'bg-blue-600';
                      case 'rejected': return 'bg-red-600';
                      case 'completed': return 'bg-green-600';
                      default: return 'bg-gray-600';
                    }
                  };

                  return (
                    <div key={reservation.reservation_id} className="glass p-6 rounded-sm">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase ${getStatusColor(reservation.status)}`}>
                              {reservation.status}
                            </span>
                            <span className="text-xs text-textSecondary">
                              {new Date(reservation.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          
                          <h3 className="font-heading font-bold text-xl mb-2">
                            {reservation.vehicle_year} {reservation.vehicle_make} {reservation.vehicle_model}
                          </h3>
                          
                          <div className="text-3xl font-heading font-bold text-primary mb-3">
                            ${(reservation.reserved_price || reservation.amount || 0).toLocaleString()}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 text-sm text-textSecondary mb-4">
                            <div><strong>Buyer:</strong> {reservation.buyer_name || reservation.user_name || 'N/A'}</div>
                            <div><strong>Vehicle ID:</strong> {reservation.vehicle_id}</div>
                          </div>
                          
                          <a
                            href={`/vehicle/${reservation.vehicle_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block bg-surfaceHighlight hover:bg-white/10 text-white rounded-sm font-medium px-4 py-2 transition-colors text-sm"
                          >
                            View Vehicle
                          </a>
                        </div>
                        
                        <div className="ml-6 flex flex-col space-y-2 min-w-[140px]">
                          {reservation.status === 'pending' && (
                            <>
                              <button
                                onClick={() => updateReservationStatus(reservation.reservation_id, 'approved')}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-sm font-bold transition-colors"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => updateReservationStatus(reservation.reservation_id, 'rejected')}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-sm text-sm font-bold transition-colors"
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {reservation.status === 'approved' && (
                            <button
                              onClick={() => updateReservationStatus(reservation.reservation_id, 'completed')}
                              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-sm text-sm font-bold transition-colors"
                            >
                              Mark Completed
                            </button>
                          )}
                          {(reservation.status === 'rejected' || reservation.status === 'completed') && (
                            <div className="text-center text-xs text-textSecondary py-2">
                              No actions
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Dealer Payments Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-6">Payment History</h2>
          
          {/* Payment Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="glass p-6 rounded-sm">
              <DollarSign className="w-8 h-8 text-green-500 mb-4" />
              <div className="text-3xl font-heading font-bold text-green-500">
                ₦{paymentStats ? paymentStats.total_revenue.toLocaleString() : '0'}
              </div>
              <div className="text-sm text-textSecondary">Total Received (NGN)</div>
            </div>
            <div className="glass p-6 rounded-sm">
              <CheckCircle className="w-8 h-8 text-green-500 mb-4" />
              <div className="text-3xl font-heading font-bold">
                {paymentStats ? paymentStats.total_successful_payments : 0}
              </div>
              <div className="text-sm text-textSecondary">Successful</div>
            </div>
            <div className="glass p-6 rounded-sm">
              <Clock className="w-8 h-8 text-yellow-500 mb-4" />
              <div className="text-3xl font-heading font-bold">
                {paymentStats ? paymentStats.pending_payments : 0}
              </div>
              <div className="text-sm text-textSecondary">Pending</div>
            </div>
            <div className="glass p-6 rounded-sm">
              <XCircle className="w-8 h-8 text-red-500 mb-4" />
              <div className="text-3xl font-heading font-bold">
                {paymentStats ? paymentStats.failed_payments : 0}
              </div>
              <div className="text-sm text-textSecondary">Failed</div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="glass p-6 rounded-sm mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-textSecondary mb-2">Search</label>
                <input
                  type="text"
                  placeholder="Buyer name, vehicle, or reference..."
                  className="w-full glass rounded-sm px-4 py-2 text-sm border border-white/10 focus:border-primary focus:outline-none"
                  value={paymentSearch}
                  onChange={(e) => setPaymentSearch(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textSecondary mb-2">Payment Status</label>
                <select
                  className="w-full glass rounded-sm px-4 py-2 text-sm border border-white/10 focus:border-primary focus:outline-none"
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="successful">Successful</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Payments Table */}
          {(payments || []).length === 0 ? (
            <div className="glass p-12 rounded-sm text-center">
              <DollarSign className="w-16 h-16 text-textSecondary mx-auto mb-4" />
              <p className="text-textSecondary">No payments received yet</p>
            </div>
          ) : (
            <div className="glass rounded-sm overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-heading font-bold">All Transactions</h3>
                <p className="text-sm text-textSecondary mt-1">
                  {(() => {
                    const filtered = (payments || []).filter(payment => {
                      const matchesSearch = !paymentSearch || 
                        payment.buyer_details?.name?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                        payment.vehicle_details?.make?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                        payment.vehicle_details?.model?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                        payment.paystack_reference?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                        payment.reference?.toLowerCase().includes(paymentSearch.toLowerCase());
                      
                      const matchesStatus = paymentStatusFilter === 'all' || payment.status === paymentStatusFilter;
                      
                      return matchesSearch && matchesStatus;
                    });
                    return `Showing ${filtered.length} of ${payments.length} transactions`;
                  })()}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surfaceHighlight">
                    <tr>
                      <th className="p-4 text-left font-heading text-sm">Buyer</th>
                      <th className="p-4 text-left font-heading text-sm">Vehicle</th>
                      <th className="p-4 text-left font-heading text-sm">Amount</th>
                      <th className="p-4 text-left font-heading text-sm">Payment Status</th>
                      <th className="p-4 text-left font-heading text-sm">Reservation</th>
                      <th className="p-4 text-left font-heading text-sm">Date</th>
                      <th className="p-4 text-left font-heading text-sm">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(payments || [])
                      .filter(payment => {
                        const matchesSearch = !paymentSearch || 
                          payment.buyer_details?.name?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                          payment.vehicle_details?.make?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                          payment.vehicle_details?.model?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                          payment.paystack_reference?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                          payment.reference?.toLowerCase().includes(paymentSearch.toLowerCase());
                        
                        const matchesStatus = paymentStatusFilter === 'all' || payment.status === paymentStatusFilter;
                        
                        return matchesSearch && matchesStatus;
                      })
                      .map((payment) => {
                        const getPaymentStatusColor = (status) => {
                          switch(status) {
                            case 'successful': return 'bg-green-600';
                            case 'pending': return 'bg-yellow-600';
                            case 'failed': return 'bg-red-600';
                            default: return 'bg-gray-600';
                          }
                        };

                        const getReservationStatusColor = (status) => {
                          switch(status) {
                            case 'approved': return 'text-green-500';
                            case 'completed': return 'text-blue-500';
                            case 'pending': return 'text-yellow-500';
                            case 'rejected': return 'text-red-500';
                            default: return 'text-gray-500';
                          }
                        };

                        return (
                          <tr key={payment.payment_id} className="border-t border-white/10 hover:bg-surfaceHighlight transition-colors">
                            <td className="p-4">
                              <div className="font-bold">{payment.buyer_details?.name || 'Unknown'}</div>
                              <div className="text-xs text-textSecondary">{payment.buyer_details?.email}</div>
                            </td>
                            <td className="p-4">
                              <div className="font-bold text-sm">
                                {payment.vehicle_details?.year} {payment.vehicle_details?.make} {payment.vehicle_details?.model}
                              </div>
                              <div className="text-xs text-textSecondary font-mono">
                                ID: {payment.vehicle_details?.vehicle_id || payment.vehicle_id}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="font-bold text-green-500">
                                ₦{payment.amount.toLocaleString()}
                              </div>
                              <div className="text-xs text-textSecondary">
                                {payment.currency || 'NGN'}
                              </div>
                            </td>
                            <td className="p-4">
                              <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase ${getPaymentStatusColor(payment.status)}`}>
                                {payment.status}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className={`text-sm font-medium uppercase ${getReservationStatusColor(payment.reservation_details?.status)}`}>
                                {payment.reservation_details?.status || 'N/A'}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="text-sm">
                                {new Date(payment.created_at).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                              <div className="text-xs text-textSecondary">
                                {new Date(payment.created_at).toLocaleTimeString('en-US', { 
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="text-xs font-mono text-textSecondary break-all max-w-[200px]">
                                {payment.paystack_reference || payment.reference}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                {(payments || []).filter(payment => {
                  const matchesSearch = !paymentSearch || 
                    payment.buyer_details?.name?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                    payment.vehicle_details?.make?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                    payment.vehicle_details?.model?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                    payment.paystack_reference?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                    payment.reference?.toLowerCase().includes(paymentSearch.toLowerCase());
                  
                  const matchesStatus = paymentStatusFilter === 'all' || payment.status === paymentStatusFilter;
                  
                  return matchesSearch && matchesStatus;
                }).length === 0 && (
                  <div className="p-12 text-center text-textSecondary">
                    <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No matching transactions found</p>
                    {paymentSearch || paymentStatusFilter !== 'all' ? (
                      <button
                        onClick={() => {
                          setPaymentSearch('');
                          setPaymentStatusFilter('all');
                        }}
                        className="mt-4 text-primary hover:text-primaryHover"
                      >
                        Clear filters
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      </div>
      
      {/* Confirmation Modal for Submit Verification */}
      {showSubmitConfirmModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-border max-w-md w-full rounded-sm p-6 space-y-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-textPrimary mb-2">
                  Confirm Verification Submission
                </h3>
                <p className="text-sm text-textSecondary">
                  You are about to submit <strong>{new Set(verificationDocs.map(d => d.type)).size} document(s)</strong> for admin verification.
                </p>
                <p className="text-sm text-yellow-500 mt-2">
                  ⚠️ Once submitted, you will not be able to edit or delete documents until admin reviews your submission.
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  console.log('🔴 User cancelled confirmation (via modal)');
                  setShowSubmitConfirmModal(false);
                }}
                disabled={submittingVerification}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmitVerification}
                disabled={submittingVerification}
                className="flex-1 bg-primary hover:bg-primaryHover text-white px-4 py-2 rounded-sm font-medium transition-colors red-glow disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {submittingVerification ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Yes, Submit</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Boost Modal */}
      {showBoostModal && boostingVehicle && (
        <BoostModal
          vehicle={boostingVehicle}
          onClose={() => {
            setShowBoostModal(false);
            setBoostingVehicle(null);
          }}
          onSuccess={() => {
            fetchDealerData();
          }}
        />
      )}
    </div>
  );
};

export default DealerDashboard;
